import {AuthModel} from './_models'
import {getCookie as getRawCookie} from '../../../../lib/cookies'
import {getStoredContextToken, clearTenantContextToken} from '../../../tenant/contextToken'
import {API_URL} from '../../../tenant/apiBase'

// Multi-tenant (fleet identity): when the platform sets the per-tenant context
// cookie, forward its value verbatim as a header so the backend can verify
// tenant context locally. The token is OPAQUE to the frontend — never parsed.
// On hisbah-ui there's often no such cookie (cross-origin SPA calling the Lumen
// API), so fall back to the token this app minted itself via the silent-refresh
// module (D6) — cookie wins where both exist (it's the platform's own signal).
const TENANT_CONTEXT_COOKIE = 'tt-tenant-context'
const TENANT_CONTEXT_HEADER = 'X-Tenant-Context'

/**
 * The tenant-context header for a request to our API that does not go through axios (whose
 * interceptor in setupAxios() stamps it everywhere else), e.g. tus-js-client's own uploads.
 */
export function tenantContextHeaders(): Record<string, string> {
  const tenantContext = getRawCookie(TENANT_CONTEXT_COOKIE) || getStoredContextToken()
  return tenantContext ? {[TENANT_CONTEXT_HEADER]: tenantContext} : {}
}

/**
 * True when a request is bound for our own API (or this page's own origin). The URL is resolved
 * exactly the way the browser will resolve it — against the page — so protocol-relative
 * (`//evil.com`), whitespace-prefixed (` https://evil.com`) and backslash (`/\evil.com`) forms
 * are judged by where they really go. Nothing in the app sets an axios `baseURL`. Anything else is
 * third-party and must never receive the session or tenant-context tokens.
 */
function isApiRequest(url: unknown): boolean {
  try {
    const target = new URL(typeof url === 'string' ? url : '', window.location.href).origin
    const apiOrigin = new URL(API_URL || window.location.origin, window.location.href).origin
    return target === apiOrigin || target === window.location.origin
  } catch {
    return false
  }
}

const AUTH_LOCAL_STORAGE_KEY = 'kt-auth-react-v'
// Primary cookie name expected by other apps
const AUTH_COOKIE_NAME = 'kt-auth-session'
// Legacy/compat cookie name kept for readers expecting the previous name
const AUTH_COOKIE_NAME_LEGACY = 'kt-auth-token'

// Optional static override: configure the cookie domain for sharing across subdomains
// e.g. REACT_APP_COOKIE_DOMAIN=.example.com
const COOKIE_DOMAIN_ENV: string | undefined = process.env.REACT_APP_COOKIE_DOMAIN

// Fleet (D14): ONE build serves every tenant host, so a single build-time COOKIE_DOMAIN can be
// wrong for whichever family the browsing host actually belongs to — a browser silently refuses
// to set a cookie whose `Domain` attribute doesn't cover the current host, so baking in
// `.dumyah.com` makes cookie writes a no-op on `*.tejaratech.com` fleet hosts (and vice versa).
//
// Derive the registrable domain from the current hostname instead of matching an allow-list. An
// allow-list of known families ('.tejaratech.com', '.dumyah.com') left every BYO custom tenant
// domain — shalati.com, dimitriscoffee.com, and any future one — falling through to the build-time
// override, which the fleet build sets to '' — so those hosts got a host-only cookie and no
// session sharing between `www.` and the apex. Mirrors `cookieDomain()` in idrisi-checkout and
// `cookie_domain()` in dumyah (system/helper/cookie.php), so all three write the SAME cookie.
//
// Multi-part public suffixes (`.com.jo`, `.co.uk`) need three labels, not two: a `Domain` of
// `.com.jo` is a public suffix and the browser rejects it outright, silently dropping the write.
const MULTI_PART_SUFFIXES = new Set([
  'com.jo', 'com.ps', 'com.sa', 'com.eg', 'com.lb', 'com.tr',
  'co.uk', 'org.uk', 'co.il', 'com.au', 'co.nz',
])

// Shared platform domains whose subdomains belong to DIFFERENT tenants (`<slug>-hisbah.tejaratech.com`)
// or different customers entirely (`*.pages.dev`). A `Domain=.tejaratech.com` session cookie would be
// readable by every tenant's storefront scripts, so these hosts get a host-only cookie — and never the
// build-time override either.
const PLATFORM_DOMAINS = ['tejaratech.com', 'pages.dev']

export const HOST_ONLY = 'host-only'

function registrableDomain(hostname: string): string {
  const host = (hostname || '').toLowerCase()
  if (!host || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return ''
  if (PLATFORM_DOMAINS.some((d) => host === d || host.endsWith(`.${d}`))) return HOST_ONLY
  const parts = host.split('.')
  if (parts.length < 2) return '' // localhost / single-label host -> host-only
  const lastTwo = parts.slice(-2).join('.')
  const labels = MULTI_PART_SUFFIXES.has(lastTwo) ? 3 : 2
  if (parts.length < labels) return ''
  return `.${parts.slice(-labels).join('.')}`
}

function cookieDomain(): string | undefined {
  try {
    // Derivation FIRST, and the build-time override only where derivation declines (localhost, an
    // IP, a single-label host). A derived domain always covers the browsing host by construction,
    // whereas a stale override does not: shipping `.dumyah.com` in the shared build would make
    // every cookie write on a `*.tejaratech.com` fleet host a silent no-op. Precedence is pinned by
    // 'uses .tejaratech.com on a fleet hisbah.* host, even with a stale .dumyah.com build-time
    // override' in AuthHelpers.test.ts.
    const derived = registrableDomain(window.location.hostname)
    if (derived === HOST_ONLY) return undefined
    if (derived) return derived
  } catch {
    /* no window (SSR/non-browser test context) */
  }
  return COOKIE_DOMAIN_ENV || undefined
}

export const __testing = {registrableDomain}

function isHttps(): boolean {
  return typeof window !== 'undefined' && window.location.protocol === 'https:'
}

function setCookie(name: string, value: string, days: number): void {
  try {
    const expiresDate = new Date()
    expiresDate.setTime(expiresDate.getTime() + days * 24 * 60 * 60 * 1000)
    const expires = `; expires=${expiresDate.toUTCString()}`
    const resolvedDomain = cookieDomain()
    const domain = resolvedDomain ? `; domain=${resolvedDomain}` : ''
    const secure = isHttps() ? '; Secure' : ''
    // Prefer SameSite=None when Secure is present (HTTPS) to maximize cross-subdomain usage
    const sameSite = isHttps() ? '; SameSite=None' : '; SameSite=Lax'
    document.cookie = `${name}=${encodeURIComponent(value || '')}${expires}; path=/${domain}${secure}${sameSite}`
  } catch (error) {
    console.error('AUTH COOKIE SAVE ERROR', error)
  }
}

function getCookie(name: string): string | undefined {
  try {
    const nameEQ = name + '='
    const ca = document.cookie.split(';')
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i]
      while (c.charAt(0) === ' ') c = c.substring(1, c.length)
      if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length, c.length))
    }
  } catch (error) {
    console.error('AUTH COOKIE READ ERROR', error)
  }
  return undefined
}

function deleteCookie(name: string): void {
  try {
    const resolvedDomain = cookieDomain()
    const domain = resolvedDomain ? `; domain=${resolvedDomain}` : ''
    const secure = isHttps() ? '; Secure' : ''
    const sameSite = '; SameSite=Lax'
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}${secure}${sameSite}`
  } catch (error) {
    console.error('AUTH COOKIE REMOVE ERROR', error)
  }
}

function ensureCookiesForToken(token: string): void {
  // Set both primary and legacy cookie names for compatibility
  setCookie(AUTH_COOKIE_NAME, token, 7)
  setCookie(AUTH_COOKIE_NAME_LEGACY, token, 7)
}

function readAnyTokenCookie(): string | undefined {
  const tokenPrimary = getCookie(AUTH_COOKIE_NAME)
  if (tokenPrimary) return tokenPrimary
  return getCookie(AUTH_COOKIE_NAME_LEGACY)
}
const getAuth = (): AuthModel | undefined => {
  // Try to read from localStorage first (existing behavior) and ensure cookies reflect it
  try {
    if (typeof localStorage !== 'undefined') {
      const lsValue: string | null = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY)
      if (lsValue) {
        const auth: AuthModel = JSON.parse(lsValue) as AuthModel
        if (auth) {
          if (auth.token) {
            // Ensure cookies exist if user deleted them
            const tokenInCookie = readAnyTokenCookie()
            if (!tokenInCookie || tokenInCookie !== auth.token) {
              ensureCookiesForToken(auth.token)
            }
          }
          return auth
        }
      }
    }
  } catch (error) {
    console.error('AUTH LOCAL STORAGE PARSE ERROR', error)
  }

  // Fallback to cookie if localStorage does not have auth and backfill localStorage
  try {
    const tokenFromCookie = readAnyTokenCookie()
    if (tokenFromCookie) {
      const auth: AuthModel = { token: tokenFromCookie }
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, JSON.stringify(auth))
        }
      } catch (error) {
        console.error('AUTH LOCAL STORAGE BACKFILL ERROR', error)
      }
      return auth
    }
  } catch (error) {
    console.error('AUTH COOKIE FALLBACK ERROR', error)
  }

  return undefined
}

const setAuth = (auth: AuthModel) => {
  try {
    if (typeof localStorage !== 'undefined') {
      const lsValue = JSON.stringify(auth)
      localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, lsValue)
    }
  } catch (error) {
    console.error('AUTH LOCAL STORAGE SAVE ERROR', error)
  }

  // Also store token in cookies for cross-subdomain access
  if (auth && auth.token) {
    ensureCookiesForToken(auth.token)
  }
}

const removeAuth = () => {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
    }
  } catch (error) {
    console.error('AUTH LOCAL STORAGE REMOVE ERROR', error)
  }

  // Remove cookie tokens (both names)
  deleteCookie(AUTH_COOKIE_NAME)
  deleteCookie(AUTH_COOKIE_NAME_LEGACY)

  // Multi-tenant identity: clear the self-minted X-Tenant-Context token (module state,
  // sessionStorage, and any pending refresh/retry timer — see contextToken.ts). Without this the
  // previous user's still-live tenant-context token keeps being stamped on every outgoing request
  // until it naturally expires, even after a different user signs in in the same tab. Also clear
  // the platform-set cookie of the same name defensively, in case it's ever present here.
  clearTenantContextToken()
  deleteCookie(TENANT_CONTEXT_COOKIE)
}


export function setupAxios(axios: any) {
  axios.defaults.headers.Accept = 'application/json'

  // Multi-tenant: stamp the opaque tenant-context token on every request bound for our API, whatever
  // the auth state. Additive — when no token is available, stamp nothing and behaviour is unchanged.
  axios.interceptors.request.use(
    (config: any) => {
      // Only stamp on requests bound for OUR API. setupAxios() configures the GLOBAL axios
      // instance, so without this guard any absolute-URL request made anywhere in the app (or by
      // a dependency riding global axios) would carry the fleet-identity token — which is
      // EdDSA-signed and carries the tenant's entitlements — to a third party.
      if (isApiRequest(config?.url)) {
        const tenantContext = getRawCookie(TENANT_CONTEXT_COOKIE) || getStoredContextToken()
        if (tenantContext) {
          config.headers = config.headers || {}
          config.headers[TENANT_CONTEXT_HEADER] = tenantContext
        }
      }
      return config
    },
    (err: any) => Promise.reject(err)
  )

  axios.interceptors.request.use(
    (config: {url?: string; headers: {Authorization: string}}) => {
      const auth = getAuth()
      // Same third-party guard as the tenant header: the session token is the supplier's whole account.
      if (auth && auth.token && isApiRequest(config?.url)) {
        config.headers.Authorization = `Bearer ${auth.token}`;
      }
      
      return config
    },
    (err: any) => {
      Promise.reject(err)
    }
  )
  axios.interceptors.response.use((response:any) => response, (error:any) => {
    if (error.response.status === 401 && !error.response.config.url.includes('/login')) {
      window.location.href = '/auth'
    } else {
      return Promise.reject(error);
    }
  });
}

export {getAuth, setAuth, removeAuth, AUTH_LOCAL_STORAGE_KEY}
